import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronLeft, ChevronRight, Eye, RefreshCw, Search, X } from 'lucide-react';
import { useSelector } from 'react-redux';

const API = process.env.REACT_APP_API_URL;

const callStatusOptions = [
  { value: 'call_back', label: 'Call Back' },
  { value: 'warm_lead', label: 'Follow Up' },
  { value: 'cold_lead', label: 'Follow Up' },
];

const callStatusLabel = val =>
  callStatusOptions.find(o => o.value === val)?.label || val || '—';

const statusBadgeClass = val => {
  switch (val) {
    case 'call_back': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'warm_lead': return 'bg-gray-100 text-gray-600 border-gray-300';
    case 'cold_lead': return 'bg-gray-100 text-gray-600 border-gray-300';
    default:          return 'bg-gray-50 text-gray-500 border-gray-200';
  }
};

const formatDate = d =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── Caller Multi-Select ───────────────────────────────────────────────────────
function CallerMultiSelect({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = id => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);

  const allSelected = options.length > 0 && value.length === options.length;
  const label = value.length === 0 || allSelected
    ? 'All'
    : value.length === 1
      ? options.find(o => o.id === value[0])?.name || '1 selected'
      : `${value.length} selected`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className={value.length === 0 || allSelected ? 'text-gray-400' : 'text-gray-700'}>{label}</span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-48 overflow-y-auto">
          {options.length === 0
            ? <p className="px-3 py-2 text-sm text-gray-400">No callers</p>
            : <>
              <label className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                <input
                  type="checkbox"
                  checked={value.length === options.length}
                  ref={el => { if (el) el.indeterminate = value.length > 0 && value.length < options.length; }}
                  onChange={() => onChange(value.length === options.length ? [] : options.map(o => o.id))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Select All</span>
              </label>
              {options.map(o => (
                <label key={o.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value.includes(o.id)}
                    onChange={() => toggle(o.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{o.name}</span>
                </label>
              ))}
            </>
          }
        </div>
      )}
    </div>
  );
}

// ── View Details Modal ────────────────────────────────────────────────────────
function ViewModal({ open, onClose, record }) {
  if (!open || !record) return null;

  const Row = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-4 py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:w-36 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-800">{value || '—'}</span>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-white/10 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-base font-semibold text-gray-800">Prospect Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-4">
          <Row label="Caller"          value={record.caller?.name || record.callerName} />
          <Row label="Case Name"       value={record.caseName} />
          <Row label="Mobile"          value={record.mobileNumber} />
          <Row label="Referred By"     value={record.referredBy} />
          <Row label="Loan Type"       value={record.loanType} />
          <Row label="Amount"          value={record.amountRequired != null ? `₹${Number(record.amountRequired).toLocaleString('en-IN')}` : null} />
          <Row label="Remarks"         value={record.remarks} />

          <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-4 py-2.5 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:w-36 flex-shrink-0">Status</span>
            {record.callStatus ? (
              <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${statusBadgeClass(record.callStatus)}`}>
                {callStatusLabel(record.callStatus)}
              </span>
            ) : <span className="text-sm text-gray-400">—</span>}
          </div>

          <Row label="Call Back Date"  value={formatDate(record.callBackDate)} />
          <Row label="Created Date"    value={formatDate(record.createdAt)} />

          {record.dispositionChecked && (
            <Row label="Disposition" value={record.dispositionRemark || 'Yes'} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ProspectList() {
  const { userInfo } = useSelector(s => s.userLogin || {});
  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  const [records, setRecords] = useState([]);
  const [assignedCallers, setAssignedCallers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [toast, setToast] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCaller, setFilterCaller] = useState([]);
  const [searchName, setSearchName] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];

  const showToast = (type, msg) => setToast({ type, msg });

  const fetchRecords = () => {
    setLoading(true);
    axios.get(`${API}/callers/prospects`, authConfig)
      .then(({ data }) => setRecords(data.items || []))
      .catch(() => showToast('error', 'Failed to load prospects.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRecords(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const myId = (userInfo?.id || userInfo?._id)?.toString();
    const isSuperAdmin = userInfo?.role?.name === 'Super Admin';
    axios.get(`${API}/users`, authConfig)
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : [];

        if (isSuperAdmin) {
          setAssignedCallers(all.map(u => ({ id: u._id?.toString(), name: u.name })));
          return;
        }

        // Build parent → children map for BFS traversal
        const childrenOf = {};
        all.forEach(u => {
          const parentId = (u.assignedTo?._id || u.assignedTo)?.toString();
          if (parentId) {
            if (!childrenOf[parentId]) childrenOf[parentId] = [];
            childrenOf[parentId].push({ id: u._id?.toString(), name: u.name });
          }
        });

        // BFS from the current user to collect all subordinates at any depth
        const result = [];
        const visited = new Set();
        const queue = [myId];
        while (queue.length > 0) {
          const cur = queue.shift();
          for (const child of (childrenOf[cur] || [])) {
            if (!visited.has(child.id)) {
              visited.add(child.id);
              result.push(child);
              queue.push(child.id);
            }
          }
        }

        if (result.length > 0) {
          setAssignedCallers(result);
        } else {
          // Regular caller: show only self
          const self = all.find(u => u._id?.toString() === myId);
          setAssignedCallers(self ? [{ id: self._id?.toString(), name: self.name }] : []);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Callers from assigned users
  const callerOptions = assignedCallers;

  const callerFilterActive = filterCaller.length > 0 && filterCaller.length < callerOptions.length;

  const filteredRecords = records.filter(row => {
    if (filterStatus && row.callStatus !== filterStatus) return false;
    if (callerFilterActive) {
      const id = row.caller?._id || row.caller || '';
      if (!filterCaller.includes(id)) return false;
    }
    if (searchName.trim()) {
      const q = searchName.trim().toLowerCase();
      const matchesName = (row.caseName || '').toLowerCase().includes(q);
      const matchesMobile = (row.mobileNumber || '').toLowerCase().includes(q);
      if (!matchesName && !matchesMobile) return false;
    }
    return true;
  });

  const hasFilters = filterStatus || callerFilterActive || searchName.trim();
  const selectCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white';

  useEffect(() => { setCurrentPage(1); }, [filterStatus, filterCaller, searchName, records.length, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
  const pageSafe = Math.min(currentPage, totalPages);
  const paginatedRecords = filteredRecords.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  return (
    <div className="px-2 py-3 sm:p-6 bg-gray-50 min-h-screen">
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-600'}`}>
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Prospects</h1>
          <p className="text-xs text-gray-500 mt-0.5">Future call-back leads (Call Back &amp; Follow Up with upcoming dates)</p>
        </div>
        <button
          onClick={fetchRecords}
          className="inline-flex items-center gap-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 text-sm font-medium px-4 py-2 rounded-lg transition-colors shadow-sm"
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="mb-3">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Search by Name / Mobile</label>
          <div className="relative max-w-sm">
            <input
              type="text"
              value={searchName}
              onChange={e => setSearchName(e.target.value)}
              placeholder="Search by case name or mobile…"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            {searchName && (
              <button
                type="button"
                onClick={() => setSearchName('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={selectCls}>
              <option value="">All</option>
              {callStatusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Caller</label>
            <CallerMultiSelect options={callerOptions} value={filterCaller} onChange={setFilterCaller} />
          </div>
        </div>
        {hasFilters && (
          <div className="mt-3 flex items-center gap-3">
            <span className="text-xs text-gray-500">{filteredRecords.length} of {records.length} records</span>
            <button
              onClick={() => { setFilterStatus(''); setFilterCaller([]); setSearchName(''); }}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium underline underline-offset-2"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-sm">
              {hasFilters ? 'No prospects match the selected filters.' : 'No upcoming prospects found.'}
            </p>
            {!hasFilters && (
              <p className="text-gray-400 text-xs mt-1">
                Leads with a future call-back date (Call Back or Follow Up status) will appear here.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr>
                  {['S.No', 'Caller', 'Case Name', 'Mobile', 'Loan Type', 'Status', 'Call Back Date', 'Created Date', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedRecords.map((row, idx) => (
                  <tr key={row._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-500">{(pageSafe - 1) * pageSize + idx + 1}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.caller?.name || row.callerName || '—'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.caseName || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.mobileNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.loanType || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      {row.callStatus ? (
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${statusBadgeClass(row.callStatus)}`}>
                          {callStatusLabel(row.callStatus)}
                        </span>
                      ) : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-blue-700">
                      {formatDate(row.callBackDate)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(row.createdAt)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setViewRecord(row)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                        title="View details"
                      >
                        <Eye size={15} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filteredRecords.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-xs text-gray-500">
                Showing {(pageSafe - 1) * pageSize + 1}–{Math.min(pageSafe * pageSize, filteredRecords.length)} of {filteredRecords.length}
              </span>
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                Rows per page
                <select
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {pageSizeOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={pageSafe <= 1}
                className="inline-flex items-center justify-center h-8 px-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs"
              >
                First
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={pageSafe <= 1}
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={15} />
              </button>
              <span className="text-xs text-gray-600 px-2">Page {pageSafe} of {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={pageSafe >= totalPages}
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={15} />
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={pageSafe >= totalPages}
                className="inline-flex items-center justify-center h-8 px-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      <ViewModal
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        record={viewRecord}
      />
    </div>
  );
}
